import { Link, useLocation, useNavigate } from "react-router";
import { Home, BookOpen, Coins, Settings, LogOut } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

export function TeacherNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItems = [
    {
      label: "Dashboard",
      path: "/teacher/dashboard",
      icon: Home,
    },
    {
      label: "Books",
      path: "/teacher/books",
      icon: BookOpen,
    },
    {
      label: "TISA Coins",
      path: "/teacher/coins",
      icon: Coins,
    },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className="bg-white shadow-sm border-b-4 border-indigo-500 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Brand */}
          <div className="flex items-center gap-3">
            <img
              src="/images/tisa-logo.png"
              alt="TISA Logo"
              className="w-10 h-10"
            />
            <div className="flex flex-col">
              <span className="text-xl font-child-friendly font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-red-600 bg-clip-text text-transparent">
                TISAverse
              </span>
              <span className="text-xs text-indigo-400 font-child-friendly">
                Teacher Portal
              </span>
            </div>
          </div>

          {/* Navigation Links */}
          <div className="flex items-center gap-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`
                    flex items-center gap-2 px-4 py-2 rounded-lg
                    font-child-friendly font-semibold text-sm
                    transition-all duration-200
                    ${
                      active
                        ? "bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md"
                        : "text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="hidden md:inline">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            <div className="hidden lg:flex flex-col items-end">
              <span className="text-sm font-child-friendly font-bold text-gray-700">
                {user?.name}
              </span>
              <span className="text-xs text-indigo-500">Teacher</span>
            </div>

            <div className="flex items-center gap-2">
              <Link
                to="/teacher/settings"
                className="p-2 rounded-full hover:bg-indigo-50 text-indigo-600 transition-colors"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </Link>

              <button
                onClick={handleLogout}
                className="p-2 rounded-full hover:bg-red-50 text-red-500 transition-colors"
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
